import { Wifi, WifiOff, Trophy, RefreshCw, Loader2, Monitor } from "lucide-react";

export function Sidebar({
  connected,
  overlayHealth,
  lastUpdate,
  gamesLastUpdate,
  availableGames,
  selectedGame,
  selectedLeg,
  rikstotoLoading,
  sorlandetDays = [],
  selectedDayKey = "",
  availableGameTypes = [],
  onDaySelect,
  onGameTypeSelect,
  onGameSelect,
  onLegSelect,
  onRefresh
}) {
  const legs = selectedGame ? Array.from({ length: selectedGame.legs }, (_, i) => i + 1) : [];
  const dayLabel = (d) => {
    try {
      const dt = new Date((d.date || "") + "T12:00:00");
      const ds = dt.toLocaleDateString("nb-NO", { weekday: "short", day: "numeric", month: "short" });
      const games = [...new Set((d.pools || []).map((p) => p.product).filter(Boolean))].join("/");
      return `${ds}${games ? ` · ${games}` : ""}`;
    } catch { return d.raceday_key; }
  };
  // Spilltype-knapper: det dagen faktisk har + alltid «Alle løp»
  const gameButtons = [...availableGameTypes, "ALL"];

  return (
    <div className="control-sidebar w-64 flex flex-col">
      <div className="p-4 border-b border-[#334155]">
        <h1 className="font-barlow text-xl font-bold tracking-tight uppercase text-white">
          Rikstoto Odds
        </h1>
        <p className="text-sm text-[#94A3B8] mt-1">Sørlandets Travpark</p>
      </div>

      {/* Connection Status */}
      <div className="p-4 border-b border-[#334155]">
        <div className="flex items-center gap-2" data-testid="backend-status">
          {connected === null ? (
            <>
              <Loader2 className="w-4 h-4 text-yellow-500 animate-spin" />
              <span className="text-sm text-yellow-500">Kobler til...</span>
            </>
          ) : connected ? (
            <>
              <Wifi className="w-4 h-4 text-green-500" />
              <span className="text-sm text-green-500">Tilkoblet</span>
            </>
          ) : (
            <>
              <WifiOff className="w-4 h-4 text-red-500" />
              <span className="text-sm text-red-500">Frakoblet</span>
            </>
          )}
        </div>
        {/* Overlay live indicator — reflects whether /overlay actually receives updates */}
        {overlayHealth && (
          <div
            className="flex items-center gap-2 mt-2"
            data-testid="overlay-status"
            title={overlayHealth.status === 'ok'
              ? `Overlay tilkoblet (${overlayHealth.clients} klient${overlayHealth.clients === 1 ? '' : 'er'})`
              : overlayHealth.status === 'stale'
              ? `Overlay tilkoblet men treg – sist aktivitet ${overlayHealth.seconds}s siden`
              : 'Ingen overlay-klient tilkoblet – åpne /overlay eller /vmix'}
          >
            <Monitor className={`w-4 h-4 ${
              overlayHealth.status === 'ok' ? 'text-green-500' :
              overlayHealth.status === 'stale' ? 'text-orange-400' :
              overlayHealth.status === 'checking' ? 'text-yellow-500' :
              'text-red-500'
            }`} />
            <span className={`text-sm ${
              overlayHealth.status === 'ok' ? 'text-green-500' :
              overlayHealth.status === 'stale' ? 'text-orange-400' :
              overlayHealth.status === 'checking' ? 'text-yellow-500' :
              'text-red-500'
            }`}>
              Overlay {
                overlayHealth.status === 'ok' ? `live (${overlayHealth.clients})` :
                overlayHealth.status === 'stale' ? `treg (${overlayHealth.seconds}s)` :
                overlayHealth.status === 'checking' ? '…' :
                'frakoblet'
              }
            </span>
          </div>
        )}
        {lastUpdate && (
          <p className="text-xs text-[#94A3B8] mt-2">
            Sist oppdatert: {lastUpdate.toLocaleTimeString()}
          </p>
        )}
      </div>

      {/* Game Selection */}
      <div className="p-4 border-b border-[#334155]">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs text-[#94A3B8] uppercase tracking-wider">
            Løpsdag
          </label>
          <button
            onClick={() => onRefresh(selectedGame?.game_type || availableGameTypes[0] || 'ALL')}
            disabled={rikstotoLoading}
            className="text-xs text-[#94A3B8] hover:text-white flex items-center gap-1"
            title="Oppdater løpsprogram"
          >
            <RefreshCw className={`w-3 h-3 ${rikstotoLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Day selector */}
        <select
          data-testid="raceday-selector"
          value={selectedDayKey || ""}
          onChange={(e) => onDaySelect && onDaySelect(e.target.value)}
          disabled={rikstotoLoading}
          className="w-full h-9 px-3 py-2 mb-3 bg-[#1E293B] border border-[#334155] text-white rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-green-500 cursor-pointer"
        >
          {sorlandetDays.length === 0 && <option value="">Laster løpsdager…</option>}
          {sorlandetDays.map((d) => (
            <option key={d.raceday_key} value={d.raceday_key}>{dayLabel(d)}</option>
          ))}
        </select>

        <label className="text-xs text-[#94A3B8] uppercase tracking-wider mb-2 block">
          Spilltype
        </label>
        {/* Game type selector — dynamisk basert på valgt dag + «Alle løp» */}
        <div className="flex gap-2 mb-2 flex-wrap">
          {gameButtons.map((gt) => (
            <button
              key={gt}
              data-testid={`game-type-${gt}`}
              onClick={() => onGameTypeSelect(gt)}
              disabled={rikstotoLoading}
              className={`px-2 py-1 text-xs font-mono rounded transition-all ${
                (gt === 'ALL' ? (selectedGame?.game_type === 'ALL' || selectedGame?.game_type === 'ALLE') : selectedGame?.game_type === gt)
                  ? 'bg-green-600 text-white'
                  : 'bg-[#1E293B] text-[#94A3B8] hover:bg-[#334155]'
              } ${rikstotoLoading ? 'opacity-50' : ''}`}
            >
              {gt === 'ALL' ? 'Alle løp' : gt}
            </button>
          ))}
        </div>
        <p className="text-xs text-[#64748B] mt-2">
          {selectedGame ? `${(selectedGame.game_type === 'ALL' || selectedGame.game_type === 'ALLE') ? 'Alle løp' : selectedGame.game_type} · ${selectedGame.legs} løp` : 'Ingen spill valgt'}
          {gamesLastUpdate && (
            <span className="ml-1">
              • Oppdatert {gamesLastUpdate.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </p>
      </div>

      {/* Leg Selection */}
      <div className="p-4 flex-1">
        <label className="text-xs text-[#94A3B8] uppercase tracking-wider mb-2 block">
          Velg Løp
        </label>
        <div className="space-y-1">
          {legs.map((leg) => (
            <div
              key={leg}
              data-testid={`leg-${leg}`}
              className={`sidebar-item ${selectedLeg === leg ? 'active' : ''}`}
              onClick={() => onLegSelect(leg)}
            >
              <Trophy className="w-4 h-4 mr-3" />
              <span className="font-mono">
                {(selectedGame?.game_type === 'ALL' || selectedGame?.game_type === 'ALLE') ? `Løp ${leg}` : `${selectedGame?.game_type}-${leg}`}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Keyboard Shortcuts */}
      <div className="p-4 border-t border-[#334155]">
        <p className="text-xs text-[#94A3B8] uppercase tracking-wider mb-2">Hurtigtaster</p>
        <div className="text-xs text-[#64748B] space-y-1">
          <div className="flex justify-between">
            <span className="bg-[#1E293B] px-1.5 py-0.5 rounded font-mono">F1</span>
            <span>Odds</span>
          </div>
          <div className="flex justify-between">
            <span className="bg-[#1E293B] px-1.5 py-0.5 rounded font-mono">F2</span>
            <span>Bong</span>
          </div>
          <div className="flex justify-between">
            <span className="bg-[#1E293B] px-1.5 py-0.5 rounded font-mono">F3</span>
            <span>Super</span>
          </div>
          <div className="flex justify-between">
            <span className="bg-[#1E293B] px-1.5 py-0.5 rounded font-mono">F4</span>
            <span>Vær</span>
          </div>
          <div className="flex justify-between">
            <span className="bg-[#1E293B] px-1.5 py-0.5 rounded font-mono">F5</span>
            <span>Sløyfe</span>
          </div>
          <div className="flex justify-between">
            <span className="bg-[#1E293B] px-1.5 py-0.5 rounded font-mono">+/-</span>
            <span>Bong Leg</span>
          </div>
        </div>
      </div>
    </div>
  );
}
